import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, SquarePen, Trash2, ArrowLeft } from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { getFaqs, createFaq, updateFaq, deleteFaq, getFaqTypes, getFaqsByTypeId } from '../../Services/ContentHub/FaqsApi';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const FaqsQna = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const location = useLocation();
    const navigate = useNavigate();
    const { faqsTypeId } = location.state || {};

    const [faqs, setFaqs] = useState([]);
    const [faqTypes, setFaqTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Delete modal state
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [idToDelete, setIdToDelete] = useState(null);

    // Modal state
    const [openModal, setOpenModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editId, setEditId] = useState(null);
    const [form, setForm] = useState({
        question: '',
        answer: '',
        faqsTypeId: faqsTypeId || ''
    });
    const [fieldErrors, setFieldErrors] = useState({});

    const getPlainText = (html) => {
        if (!html) return '';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return (tempDiv.textContent || tempDiv.innerText || '').trim();
    };

    const fetchFaqs = useCallback(async () => {
        try {
            setLoading(true);
            let response;
            if (faqsTypeId) {
                response = await getFaqsByTypeId(faqsTypeId);
                if (response && response.success) {
                    setFaqs(response.data || []);
                }
            } else {
                response = await getFaqs();
                if (response && response.success) {
                    setFaqs(response.data);
                }
            }

            if (!response || !response.success) {
                setError(response?.message || "Failed to load FAQs.");
            }
        } catch (err) {
            setError("Failed to fetch FAQs.");
        } finally {
            setLoading(false);
        }
    }, [faqsTypeId]);

    const fetchFaqTypes = useCallback(async () => {
        try {
            const response = await getFaqTypes();
            if (response && response.success) {
                setFaqTypes(response.data);
            }
        } catch (err) {
            console.error("Failed to fetch FAQ types for dropdown");
        }
    }, []);

    useEffect(() => {
        fetchFaqs();
        fetchFaqTypes();
    }, [fetchFaqs, fetchFaqTypes]);

    const typeName = faqsTypeId ? faqTypes.find(t => t.id === faqsTypeId)?.name : '';

    const handleOpenModal = (faq = null) => {
        setFieldErrors({});
        if (faq) {
            setForm({
                question: faq.question || '',
                answer: faq.answer || '',
                faqsTypeId: faq.faqsTypeId || ''
            });
            setEditId(faq.id);
        } else {
            setForm({ question: '', answer: '', faqsTypeId: faqsTypeId || '' });
            setEditId(null);
        }
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setFieldErrors({});
        setError(null);
    };

    const handleInputChange = (name, value) => {
        setForm(prev => ({ ...prev, [name]: value }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async () => {
        const errors = {};
        if (!form.faqsTypeId) {
            errors.faqsTypeId = "FAQ Type is required";
        }
        if (!form.question.trim()) {
            errors.question = "Question is required";
        }
        if (!getPlainText(form.answer).trim()) {
            errors.answer = "Answer is required";
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }
        setFieldErrors({});

        setSubmitting(true);
        try {
            let response;
            if (editId) {
                response = await updateFaq(editId, form);
            } else {
                response = await createFaq(form);
            }

            if (response && response.success) {
                showSnackbar(editId ? "FAQ updated successfully!" : "FAQ created successfully!", "success");
                handleCloseModal();
                fetchFaqs();
            } else {
                showSnackbar(response?.message || "Failed to save FAQ", "error");
            }
        } catch (err) {
            console.error(err);
            const serverError = err.response?.data;
            const errMsg = serverError?.errors?.[0] ||
                serverError?.details?.[0]?.message ||
                serverError?.message ||
                err.message ||
                "An error occurred while saving FAQ";

            showSnackbar(errMsg, "error");

            const errorsObj = {};
            if (serverError && serverError.details && Array.isArray(serverError.details)) {
                serverError.details.forEach(item => {
                    const fieldName = item.field === 'general' ? 'question' : item.field;
                    errorsObj[fieldName] = item.message;
                });
            } else if (serverError && serverError.errors && Array.isArray(serverError.errors) && serverError.errors.length > 0) {
                errorsObj['question'] = serverError.errors[0];
            } else {
                errorsObj['question'] = errMsg;
            }
            setFieldErrors(errorsObj);
        } finally {
            setSubmitting(false);
        }
    };

    const handleOpenDelete = (id) => {
        setIdToDelete(id);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!idToDelete) return;

        try {
            const response = await deleteFaq(idToDelete);
            if (response && response.success) {
                showSnackbar("FAQ deleted successfully!", "success");
                fetchFaqs();
            } else {
                showSnackbar(response.message || "Failed to delete FAQ", "error");
            }
        } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.message || err.message || "An error occurred while deleting FAQ";
            showSnackbar(errMsg, "error");
        } finally {
            setDeleteModalOpen(false);
            setIdToDelete(null);
        }
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ color: '#444', flexShrink: 0 }}>
                    <ArrowLeft size={24} />
                </IconButton>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            color: '#222',
                            fontSize: { xs: '1.75rem', md: '1.8rem' },
                            fontWeight: 500,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                        }}
                    >
                        {typeName ? `Faqs for ${typeName}` : 'Faqs (Q&A)'}
                    </Typography>
                </Box>
                <PermissionWrapper permission="create" mode="disable">
                    <Button
                        disableElevation disableRipple disableFocusRipple disableTouchRipple
                        variant="contained"
                        startIcon={<Plus size={18} />}
                        onClick={() => handleOpenModal()}
                        sx={{
                            bgcolor: '#FFBD65',
                            px: 2.5,
                            color: '#fff',
                            borderRadius: '6px',
                            textTransform: 'none',
                            height: '40px',
                            fontSize: '0.85rem',
                            flexShrink: 0,
                            minWidth: 'fit-content'
                        }}
                    >
                        Add FAQ
                    </Button>
                </PermissionWrapper>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 1050 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center', width: '60px' }}>S.No</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '150px' }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap' }}>Question</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', textAlign: 'center' }}>Answer</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Created By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '130px' }}>Updated By</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Created At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '120px' }}>Updated At</TableCell>
                                <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.5, whiteSpace: 'nowrap', width: '100px', textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : faqs.length > 0 ? (
                                faqs.map((faq, idx) => (
                                    <TableRow key={faq.id} sx={{
                                        transition: 'background-color 0.2s',
                                        '& td': { borderBottom: '1px solid #F0F0F0' },
                                        '&:last-child td': { borderBottom: 0 }
                                    }}>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#666', textAlign: 'center' }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#333', fontWeight: 500 }}>
                                            {faqTypes.find(t => t.id === faq.faqsTypeId)?.name || faq.faqsTypeId}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#333', maxWidth: '250px' }}>
                                            <Typography sx={{ fontWeight: 600, color: '#333', fontSize: '0.8rem' }}>{faq.question}</Typography>
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#555', maxWidth: '350px', textAlign: 'center' }}>
                                            <Typography
                                                sx={{
                                                    color: '#666',
                                                    fontSize: '0.8rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    display: '-webkit-box',
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: 'vertical',
                                                    '& p': { m: 0 }
                                                }}
                                                dangerouslySetInnerHTML={{ __html: faq.answer }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {faq.cuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {faq.uuName || '-'}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(faq.createdAt)}
                                        </TableCell>
                                        <TableCell sx={{ py: 1.25, px: 1.5, fontSize: '0.8rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                            {formatDate(faq.updatedAt)}
                                        </TableCell>
                                        <TableCell align="center" sx={{ py: 1.25, px: 1.5 }}>
                                            <Stack direction="row" spacing={0.5} justifyContent="center">
                                                <PermissionWrapper permission="edit" mode="disable">
                                                    <Tooltip title="Edit">
                                                        <IconButton disableFocusRipple disableRipple disableTouchRipple onClick={() => handleOpenModal(faq)} size="small" sx={{ color: '#FFBD65' }}>
                                                            <SquarePen size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </PermissionWrapper>
                                                <PermissionWrapper permission="delete" mode="disable">
                                                    <Tooltip title="Delete">
                                                        <IconButton disableFocusRipple disableRipple disableTouchRipple onClick={() => handleOpenDelete(faq.id)} size="small" sx={{ color: '#FF4D49' }}>
                                                            <Trash2 size={16} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </PermissionWrapper>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#AAA', fontSize: '0.8rem' }}>
                                        No FAQs found.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                <DialogTitle sx={{ fontWeight: 600, color: '#222', fontSize: '1.1rem', pb: 1 }}>{editId ? 'Edit FAQ' : 'Add FAQ'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="FAQ Type *"
                            value={form.faqsTypeId}
                            onChange={(e) => handleInputChange('faqsTypeId', e.target.value)}
                            error={Boolean(fieldErrors.faqsTypeId)}
                            helperText={fieldErrors.faqsTypeId}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        >
                            {faqTypes.map((type) => (
                                <MenuItem key={type.id} value={type.id} sx={{ fontSize: '0.85rem' }}>
                                    {type.name} ({type.faqsCategories})
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            fullWidth
                            size="small"
                            label="Question *"
                            multiline
                            rows={2}
                            value={form.question}
                            onChange={(e) => handleInputChange('question', e.target.value)}
                            error={Boolean(fieldErrors.question)}
                            helperText={fieldErrors.question}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                        />
                        <Box sx={{
                            '& .ql-toolbar': {
                                borderRadius: '6px 6px 0 0',
                                borderColor: '#E0E0E0'
                            },
                            '& .ql-container': {
                                borderRadius: '0 0 6px 6px',
                                borderColor: '#E0E0E0',
                                minHeight: '180px'
                            }
                        }}>
                            <Typography sx={{ mb: 1, fontWeight: 550, color: fieldErrors.answer ? '#FF4D49' : '#333', fontSize: '0.85rem' }}>
                                Answer *
                            </Typography>
                            <ReactQuill
                                theme="snow"
                                value={form.answer}
                                onChange={(value) => handleInputChange('answer', value)}
                                style={{ height: '180px', marginBottom: '45px' }}
                                modules={{
                                    toolbar: [
                                        [{ 'header': [1, 2, false] }],
                                        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                                        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
                                        ['link', 'clean']
                                    ],
                                }}
                            />
                            {fieldErrors.answer && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block', fontSize: '0.75rem' }}>
                                    {fieldErrors.answer}
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 2.5, pt: 1 }}>
                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem', px: 2.5 }}>Cancel</Button>
                    <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                        onClick={handleSubmit}
                        disabled={submitting}
                        variant="contained"
                        sx={{
                            bgcolor: '#FFBD65',
                            borderRadius: '6px',
                            textTransform: 'none',
                            px: 3,
                            color: '#fff',
                            fontSize: '0.85rem'
                        }}
                    >
                        {submitting ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <DeleteModal
                open={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onDelete={handleConfirmDelete}
                title="Delete FAQ"
                description="Are you sure you want to delete this FAQ? This action cannot be undone."
            />
        </Box>
        </PermissionWrapper>
    );
};

export default FaqsQna;
